import type { Response } from '../admission-grid-2'
import type { UseMutationResult } from '@tanstack/react-query'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import http from '../../../../../../../lib/http'
import { useInvalidateIGAO } from '../../../../../../igao/hooks/invalidate-igao'

interface Variables {
  id: string
}

interface Props {
  patientId?: string
}

export function useDeleteAdmissionMutation ({ patientId }: Props): UseMutationResult<unknown, unknown, Variables> {
  const queryClient = useQueryClient()
  let invalidateIGAO = useInvalidateIGAO('admissions')

  return useMutation<unknown, unknown, Variables>({
    async mutationFn (variables) {
      if (!patientId) {
        return
      }
      return await http.deleteRequest(`/patients/${patientId}/admission/${variables.id}`)
    },
    async onMutate (variables) {
      queryClient.setQueriesData<Response>(
        { queryKey: [ 'admissions-grid', patientId as string ] },
        (current) => {
          return {
            ...current,
            empty: current?.empty ?? false,
            entries: [
              ...current?.entries.filter((x) => x.id !== variables.id) ?? []
            ] ?? []
          }
        })
    },
    async onSettled () {
      await queryClient.invalidateQueries({ queryKey: [ 'admissions-grid', patientId as string ] })
      await invalidateIGAO()
      await queryClient.invalidateQueries({
        predicate ({ queryKey }) {
          return Array.isArray(queryKey) && queryKey[0] === 'claims-history/admissions'
        }
      })
    },
    async onError () {
      void queryClient.invalidateQueries({ queryKey: [ 'admissions-grid', patientId as string ] })
    }
  })
}
